import api from '@/api';
import axios from 'axios';
import { createStore } from 'vuex';

const baseURL = process.env.VUE_APP_API_BASE_URL;

/**
 * Decode JWT safely
 */
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch (e) {
    return null;
  }
}

/**
 * Check expiration
 */
function isTokenExpired(token) {
  const decoded = parseJwt(token);
  if (!decoded?.exp) return false;

  const now = Date.now() / 1000;
  return decoded.exp < now;
}

export default createStore({
  state: {
    token: localStorage.getItem('token') || null,
    user: null,
    isAuthenticated: !!localStorage.getItem("token"),
    permissions: [],
    roles: []
  },

  mutations: {

    setToken(state, token) {
      state.token = token;
      localStorage.setItem('token', token);
    },

    setUser(state, user) {
      state.user = user;
      state.permissions = user.permissions || [];
      state.roles = user.roles || [];
    },

    setAuthenticated(state, value = true) {
      state.isAuthenticated = value;
    },

    logout(state) {
      state.token = null;
      state.user = null;
      state.permissions = [];
      state.roles = [];
      state.isAuthenticated = false;

      localStorage.removeItem('token');
    }
  },

  actions: {

    /**
     * LOGIN JWT
     */
    async authenticate({ commit }, credentials) {
      try {
        const loginResponse = await axios.post(
          `${baseURL}/api/login`,
          credentials
        );

        const token = loginResponse.data.token;
        if (!token) return { success: false };

        // ❌ token expiré
        if (isTokenExpired(token)) {
          return { success: false, expired: true };
        }

        commit('setToken', token);

        /**
         * 🔥 Cas 1 : si ton JWT contient user (decoded)
         */
        const decoded = parseJwt(token);

        if (decoded && (decoded.roles || decoded.permissions)) {
          commit('setUser', decoded);
          commit('setAuthenticated', true);
          return { success: true };
        }

        /**
         * 🔥 Cas 2 : fallback Symfony /me (plus fiable)
         */
        const meResponse = await axios.get(
          `${baseURL}/api/v1/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const user = meResponse.data;

        if (user.enabled === false) {
          return { success: false, disabled: true };
        }

        commit('setUser', user);
        commit('setAuthenticated', true);

        return { success: true };

      } catch (error) {
        console.error('Login error:', error.response?.data || error.message);
        return { success: false };
      }
    },

    /**
     * REFRESH USER (source unique /me)
     */
    async refreshUser({ commit, dispatch, state }) {
      if (!state.token) return false;

      try {
        const response = await axios.get(
          `${baseURL}/api/v1/me`,
          {
            headers: {
              Authorization: `Bearer ${state.token}`
            }
          }
        );

        const user = response.data;

        if (user.enabled === false) {
          dispatch('logout');
          throw new Error('Compte désactivé');
        }

        commit('setUser', user);

        return true;

      } catch (error) {
        dispatch('logout');
        return false;
      }
    },

    logout({ commit }) {
      commit('logout');
    }
  },

  getters: {

    isAuthenticated: (state) =>
      !!state.token && state.isAuthenticated,

    user: (state) => state.user,

    /**
     *  SUPER ADMIN
     */
    isSuperAdmin: (state) =>
      (state.roles || []).includes('ROLE_SUPER_ADMIN'),

    /**
     *  permission unique
     */
    hasPermission: (state, getters) => (permission) => {
      if (getters.isSuperAdmin) return true;

      return (state.permissions || [])
        .map(p => p.toLowerCase())
        .includes(permission.toLowerCase());
    },

    /**
     *  plusieurs permissions
     */
    hasAnyPermission: (state, getters) => (permissions = []) => {
      if (getters.isSuperAdmin) return true;

      const userPermissions = (state.permissions || []).map(p => p.toLowerCase());

      return permissions.some(p =>
        userPermissions.includes(p.toLowerCase())
      );
    },

    /**
     *
     */
    hasRole: (state) => (role) => {
      const normalized = role.toUpperCase().replace(/^ROLE_/, '');

      return (state.roles || []).some(r =>
        r.toUpperCase().replace(/^ROLE_/, '') === normalized
      );
    }
  }
});